export type ClarifyMainStatus = 'request' | 'in_progress' | 'in_review' | 'solved';

export type ClarifyDetailStatus =
  | 'request'
  | 'qualified'
  | 'assigned'
  | 'accepted'
  | 'interview_in_progress'
  | 'awaiting_input'
  | 'submitted'
  | 'review_in_progress'
  | 'revision_requested'
  | 'solved'
  | 'rejected'
  | 'cancelled'
  | 'expired';

export type ClarifyPriority = 'low' | 'medium' | 'high' | 'critical';
export type ClarifyTitleSuggestionState = 'idle' | 'loading' | 'ready' | 'fallback';

export interface ClarifyPerson {
  userId: string;
  displayName: string;
  email?: string | null;
}

export interface ClarifyLinkedDocument {
  id: string;
  fileName: string;
}

export interface ClarifyTimelineEntry {
  id: string;
  eventType: string;
  actorDisplayName: string;
  description: string;
  note?: string | null;
  createdAt: string;
}

export interface ClarifyRequestSummary {
  id: string;
  requestNumber: string;
  title: string;
  question: string;
  businessContext?: string | null;
  tags: string[];
  mainStatus: ClarifyMainStatus;
  detailStatus: ClarifyDetailStatus;
  priority: ClarifyPriority;
  requester: ClarifyPerson;
  assignee?: ClarifyPerson | null;
  reviewer?: ClarifyPerson | null;
  createdAt: string;
  updatedAt: string;
  dueDate?: string | null;
  adminNote?: string | null;
  interviewTaskNumber?: string | null;
  interviewSessionId?: string | null;
  linkedDocuments: ClarifyLinkedDocument[];
}

export interface ClarifyRequestDetail extends ClarifyRequestSummary {
  timeline: ClarifyTimelineEntry[];
}

export interface ClarifyListResponse {
  items: ClarifyRequestSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ClarifyDashboardData {
  requestCount: number;
  inProgressCount: number;
  inReviewCount: number;
  solvedCount: number;
  urgent: ClarifyRequestSummary[];
  dueSoon: ClarifyRequestSummary[];
  overdue: ClarifyRequestSummary[];
  recentActivity: ClarifyRequestSummary[];
}

export interface ClarifyWorkspaceMember {
  userId: string;
  displayName: string;
  email?: string | null;
  role: string;
  isOrgAdmin: boolean;
}

export interface CreateClarifyRequestInput {
  title: string;
  question: string;
  businessContext?: string;
  tags: string[];
  linkedDocumentIds?: string[];
  priority: ClarifyPriority;
}

export interface SuggestClarifyTitleInput {
  question: string;
}

export interface SuggestClarifyTitleResponse {
  title: string;
  source: 'llm' | 'fallback';
}

export interface SuggestClarifyDraftMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface SuggestClarifyDraftInput {
  question: string;
  answer: string;
  chatHistory?: SuggestClarifyDraftMessage[];
  referencedSources?: string[];
}

export interface SuggestClarifyDraftResponse {
  title: string;
  businessContext: string;
  titleSource: 'llm' | 'fallback';
  businessContextSource: 'llm' | 'fallback';
}

export interface TriageClarifyRequestInput {
  assigneeUserId: string;
  dueDate?: string;
  priority?: ClarifyPriority;
  adminNote?: string;
}

export interface ReviewClarifyRequestInput {
  action: 'approve' | 'revision' | 'reject';
  comment?: string;
}

export interface RejectClarifyRequestInput {
  comment?: string;
}

export type ClarifyListView = 'my-requests' | 'my-tasks' | 'triage' | 'review';

export interface ClarifyListState {
  items: ClarifyRequestSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const EMPTY_CLARIFY_LIST: ClarifyListState = {
  items: [],
  totalCount: 0,
  page: 1,
  pageSize: 50,
  totalPages: 0,
};